import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PoundSterling, CalendarDays, CalendarRange, Clock, Truck, Package, ScanLine, AlertTriangle } from "lucide-react";
import { api, gbp, ukDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import PriceAlerts from "../components/dashboard/PriceAlerts";
import MarketNotes from "../components/dashboard/MarketNotes";
import LastYear from "../components/dashboard/LastYear";

function Stat({ icon: Icon, label, value, tone = "text-slate-900", testId, to }) {
  const body = (
    <div className="stat-card h-full" data-testid={testId}>
      <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><Icon className="h-4 w-4 text-slate-400" /></div>
      <p className={`mt-3 text-2xl sm:text-3xl font-display font-bold ${tone}`}>{value}</p>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

export default function Dashboard() {
  const { restaurantId, user, can } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/dashboard", { params: { restaurant_id: restaurantId || undefined } }).then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => { alive = false; clearInterval(t); };
  }, [restaurantId]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Good ${new Date().getHours() < 12 ? "morning" : "afternoon"}, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Price moves, market notes and where your invoices stand."
        actions={<Button asChild className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5"><Link to="/scan" data-testid="dashboard-scan-button"><ScanLine className="mr-2 h-4 w-4" /> Scan invoices</Link></Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 fade-up">
        <Stat icon={CalendarDays} label="This week" value={data ? gbp(data.spend_this_week) : "—"} testId="stat-spend-week" />
        <Stat icon={PoundSterling} label="This month" value={data ? gbp(data.spend_this_month) : "—"} testId="stat-spend-month" />
        <Stat icon={CalendarRange} label="Last month" value={data ? gbp(data.spend_last_month) : "—"} testId="stat-spend-last-month" />
        <Stat icon={Clock} label="Needs review" value={data?.needs_review ?? "—"} tone="text-amber-600" testId="stat-needs-review" to="/invoices?status=needs_review" />
        <Stat icon={Truck} label="Suppliers" value={data?.suppliers ?? "—"} testId="stat-suppliers" to="/suppliers" />
        <Stat icon={Package} label="Supplier products" value={data?.supplier_products ?? "—"} testId="stat-products" to="/products" />
      </div>

      {data?.failed > 0 && (
        <Link to="/invoices?status=failed" className="mt-4 flex items-center gap-2 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-4 py-3" data-testid="dashboard-failed-banner">
          <AlertTriangle className="h-4 w-4" /> {data.failed} invoice{data.failed > 1 ? "s" : ""} failed processing — tap to retry or enter manually.
        </Link>
      )}

      {can("view_prices") && (
        <div className="mt-6 grid gap-4 lg:grid-cols-3 fade-up">
          <div className="lg:col-span-2 space-y-4">
            <PriceAlerts restaurantId={restaurantId} />
            <LastYear restaurantId={restaurantId} />
          </div>
          <MarketNotes restaurantId={restaurantId} />
        </div>
      )}

      <section className="mt-8 fade-up">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Recent invoices</h2>
          <Link to="/invoices" className="text-sm text-emerald-700 hover:underline" data-testid="view-all-invoices-link">View all</Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {!data ? <p className="p-6 text-sm text-slate-500">Loading…</p> : data.recent_invoices.length === 0 ? (
            <div className="p-10 text-center" data-testid="dashboard-empty">
              <p className="font-medium text-slate-800">No invoices yet</p>
              <p className="text-sm text-slate-500 mt-1">Scan or upload your first supplier invoice to get started.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_invoices.map((inv) => (
                <li key={inv.id}>
                  <Link to={`/invoices/${inv.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`recent-invoice-${inv.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{inv.supplier_name || inv.extracted_supplier?.name || inv.original_filename || "Processing…"}</p>
                      <p className="text-xs text-slate-500 font-mono">{inv.invoice_number ? `#${inv.invoice_number}` : "—"} · {ukDate(inv.invoice_date || inv.created_at)}</p>
                    </div>
                    <span className="font-mono text-sm text-slate-800 hidden sm:block">{gbp(inv.total, inv.currency)}</span>
                    <StatusBadge status={inv.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
